import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationsPanel from './NotificationsPanel';
import GlobalSearch from './GlobalSearch';
import { HEADER_ACTIONS_ID } from './ui/dg';
import { usePendingItems } from '../hooks/usePendingItems';

const MenuIcon = () => (
  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

interface HeaderProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  title?: string;
  subtitle?: string;
}

const Header: React.FC<HeaderProps> = ({
  sidebarOpen,
  setSidebarOpen,
  title = "My Dashboard",
  subtitle
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  // La cloche et la page « À traiter » lisent la même source : deux vues du
  // même contenu doivent compter et ordonner de la même façon.
  const { items: notifications, loading: notificationsLoading } = usePendingItems();
  const notificationsRef = useRef<HTMLDivElement>(null);
  const pendingCount = notifications.length;
  const isStaff = user?.role === 'admin' || user?.role === 'moderator';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="flex h-[76px] shrink-0 items-center gap-3 bg-[#F7F7FB] px-4 lg:px-7">
      {/* Bouton menu pour mobile */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="rounded-full p-2 hover:bg-white lg:hidden"
        aria-label="Ouvrir la navigation"
      >
        <MenuIcon />
      </button>
      {/* Le titre seul, en grand, comme les pages de l'app. La phrase qui le
          suivait répétait ce que la page montre déjà. */}
      <h1 title={subtitle} className="min-w-0 flex-1 truncate text-[22px] font-extrabold tracking-[-0.4px] text-[#1E293B] lg:text-[26px]">{title}</h1>
      {/* Ce que la page pose à côté de son titre : onglets, période, filtres. */}
      <div id={HEADER_ACTIONS_ID} className="hidden items-center gap-2 lg:flex" />
      <GlobalSearch />
      {/* Cloche : tickets ouverts et vérifications en attente */}
      {isStaff && (
        <div className="relative" ref={notificationsRef}>
          <button
            onClick={() => setNotificationsOpen((open) => !open)}
            title="Ce qui attend d'être traité"
            aria-label="Ce qui attend d'être traité"
            className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[#E5E7EB] bg-white hover:bg-gray-50"
          >
            <svg width="18" height="18" fill="none" stroke="#1E293B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" />
              <path d="M10 20a2 2 0 0 0 4 0" />
            </svg>
            {pendingCount > 0 && (
              <span className="absolute right-[9px] top-[8px] h-2 w-2 rounded-full border-2 border-white bg-[#EF4444]" />
            )}
          </button>
          {notificationsOpen && (
            <NotificationsPanel
              items={notifications}
              loading={notificationsLoading}
              onSelect={(item) => {
                setNotificationsOpen(false);
                navigate(item.target);
              }}
              onSeeAll={() => {
                setNotificationsOpen(false);
                navigate('/a-traiter');
              }}
            />
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
